import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Trash2, FileText, Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [streak, setStreak] = useState({ streak: 0, total_days: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [recs, st] = await Promise.all([
        api.get("/recommendations"),
        api.get("/tracker/streak"),
      ]);
      setItems(recs.data);
      setStreak(st.data);
    } catch {
      toast.error("Could not load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this recommendation?")) return;
    try {
      await api.delete(`/recommendations/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <main className="max-w-5xl mx-auto px-6 sm:px-8 py-12 sm:py-16" data-testid="dashboard-page">
      <header className="mb-12 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="label-eyebrow mb-2">Welcome back, {user?.name?.split(" ")[0]}</p>
          <h1 className="font-serif text-4xl sm:text-5xl leading-tight">Your wellness journey</h1>
        </div>
        <Link to="/questionnaire" className="btn-primary flex items-center gap-2" data-testid="dashboard-new">
          <Plus className="w-4 h-4" strokeWidth={1.5} /> New assessment
        </Link>
      </header>

      <section className="grid sm:grid-cols-3 gap-4 mb-12">
        <div className="card-soft p-6" data-testid="stat-streak">
          <p className="label-eyebrow mb-2">Current streak</p>
          <p className="font-serif text-4xl">{streak.streak}</p>
          <p className="text-sm" style={{ color: "var(--text-2)" }}>days in a row</p>
        </div>
        <div className="card-soft p-6" data-testid="stat-total">
          <p className="label-eyebrow mb-2">Days logged</p>
          <p className="font-serif text-4xl">{streak.total_days}</p>
          <p className="text-sm" style={{ color: "var(--text-2)" }}>total tracked</p>
        </div>
        <div className="card-soft p-6" data-testid="stat-plans">
          <p className="label-eyebrow mb-2">Plans</p>
          <p className="font-serif text-4xl">{items.length}</p>
          <p className="text-sm" style={{ color: "var(--text-2)" }}>assessments saved</p>
        </div>
      </section>

      <h2 className="font-serif text-2xl mb-5">Past assessments</h2>

      {loading ? (
        <p style={{ color: "var(--text-2)" }}>Loading…</p>
      ) : items.length === 0 ? (
        <div className="card-soft p-10 text-center">
          <p className="font-serif text-2xl mb-2">No assessments yet</p>
          <p className="mb-6" style={{ color: "var(--text-2)" }}>
            Start your first questionnaire to receive a personalised plan.
          </p>
          <Link to="/questionnaire" className="btn-primary" data-testid="dashboard-empty-cta">
            Start assessment
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div
              key={item.id}
              className="card-soft p-5 sm:p-6 flex flex-wrap items-center justify-between gap-3"
              data-testid={`history-item-${item.id}`}
            >
              <div>
                <p className="label-eyebrow mb-1">
                  {new Date(item.created_at).toLocaleDateString(undefined, { dateStyle: "long" })}
                </p>
                <p className="font-serif text-xl">
                  {item.supplements.length} supplements ·{" "}
                  <span style={{ color: "var(--text-2)" }}>
                    {item.profile?.concerns?.slice(0, 3).join(", ").replace(/_/g, " ")}
                  </span>
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Link
                  to={`/recommendations/${item.id}`}
                  className="btn-ghost text-sm flex items-center gap-1.5"
                  data-testid={`history-view-${item.id}`}
                >
                  <FileText className="w-4 h-4" strokeWidth={1.5} /> View
                </Link>
                <button
                  onClick={() => del(item.id)}
                  className="btn-ghost text-sm flex items-center gap-1.5"
                  style={{ color: "var(--error)" }}
                  data-testid={`history-delete-${item.id}`}
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
